import { BrowserRouter as Router, Routes, Route, useLocation, useNavigate, Navigate } from "react-router-dom";
import {
  FluentProvider,
  Menu,
  MenuItem,
  MenuList,
  MenuPopover,
  MenuTrigger,
  Button,
  webDarkTheme,
  webLightTheme,
} from "@fluentui/react-components";

import NavigationPanel from "./shared/components/NavigationPanel";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { tokens } from "@fluentui/react-theme";
import { useTranslation } from "react-i18next";
import { useEffect, useState } from "react";

// Components
import { ErrorDialogProvider } from "./context/ErrorDialogContext";
import UserAvatar from "./features/users/components/UserAvatar";
import AuthProvider from "./provider/authProvider";
import { useAuth } from "./provider/authProvider";
import { ErrorDialog } from "./shared/components/ErrorDialog";
import { Toaster } from "@fluentui/react-components";
import { TOASTER_ID } from "./shared/components/SuperToast";
import Logo from "./features/tenants/components/Logo";

// Pages
import AuthPage from "./features/auth/components/AuthPage";
import IdentityActivate from "./features/identity/components/IdentityActivate";
import IdentityOnboarding from "./features/identity/components/IdentityOnboarding";
import IdentityPasswordReset from "./features/identity/components/PasswordReset";
import Register from "./features/identity/components/Register";
import CachePage from "./shared/components/CachePage";
import Ci from "./features/ci/components/Ci";
import CiEditor from "./features/ci/components/CiEditor";
import Data from "./pages/Data";
import FeatureNotImplemented from "./pages/FeatureNotImplemented";
import Home from "./pages/Home";
import NotFound from "./pages/NotFound";
import Logout from "./features/auth/components/Logout";
import Settings from "./pages/Settings";
import PreferencesDrawer from "./features/preferences/components/PreferencesDrawer";
import Tickets from "./features/tickets/components/Tickets";
import TicketEditor from "./features/tickets/components/TicketEditor";
import TenantLogoRedirect from "./features/tenants/components/TenantLogoRedirect";
import { useCurrentTenantQuery, useTenantBySlugQuery } from "./features/tenants/hooks/useTenantQueries";

// Variables
const queryClient = new QueryClient();

function getDocumentTitle(tenantName) {
  const env = process.env.NODE_ENV;
  const safeTenantName = tenantName?.trim() || "easyITSM";
  return env !== "production" ? `${safeTenantName} [${env}]` : safeTenantName;
}

function resolveTenantLogoUrl(value) {
  if (!value || typeof value !== "string") return null;
  if (/^https?:\/\//i.test(value)) return value;

  const minioHost = process.env.REACT_APP_MINIO_PUBLIC_HOST || process.env.REACT_APP_MINIO_HOST || "minio";
  const minioPort = process.env.REACT_APP_MINIO_PUBLIC_PORT || process.env.REACT_APP_MINIO_PORT || "9000";
  const minioProtocol = process.env.REACT_APP_MINIO_PUBLIC_PROTOCOL || "http";
  const objectName = value.replace(/^\/+/, "");
  return `${minioProtocol}://${minioHost}:${minioPort}/public/${objectName}`;
}

function setFaviconHref(iconUrl) {
  const defaultFavicon = `${process.env.PUBLIC_URL || ""}/favicon.ico`;
  const href = iconUrl || defaultFavicon;
  let link = document.querySelector("link[rel='icon']") || document.querySelector("link[rel~='icon']");

  if (!link) {
    link = document.createElement("link");
    link.setAttribute("rel", "icon");
    document.head.appendChild(link);
  }

  link.setAttribute("href", href);
}

export const AccountMenu = ({ onOpenPreferences }) => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { user: currentUser } = useAuth();

  return (
    <Menu positioning="below-end">
      <MenuTrigger disableButtonEnhancement>
        <Button appearance="transparent" aria-label="User menu" icon={<UserAvatar user={currentUser} size={28} />} />
      </MenuTrigger>
      <MenuPopover>
        <MenuList>
          <MenuItem onClick={onOpenPreferences}>{t("generic.preferences")}</MenuItem>
          <MenuItem onClick={() => navigate("/logout")}>{t("generic.logout")}</MenuItem>
        </MenuList>
      </MenuPopover>
    </Menu>
  );
};

// Layout wrapper for authenticated pages
function AppLayout({ themeName, setThemeName }) {
  const { t } = useTranslation();
  const [isPreferencesOpen, setIsPreferencesOpen] = useState(false);
  const { data: currentTenant } = useCurrentTenantQuery();

  const tenantName = currentTenant?.name?.trim() || "easyITSM";

  const [isDrawerMinimized, setDrawerMinimized] = useState(() => {
    return localStorage.getItem("drawerMinimized") === "true";
  });

  useEffect(() => {
    document.title = getDocumentTitle(tenantName);
  }, [tenantName]);

  useEffect(() => {
    const tenantLogoUrl = resolveTenantLogoUrl(currentTenant?.logo_ath || currentTenant?.logo_path || null);
    setFaviconHref(tenantLogoUrl);
  }, [currentTenant?.logo_ath, currentTenant?.logo_path]);

  return (
    <div style={{ height: "100vh", display: "flex", flexDirection: "column" }}>
      {/* Header */}
      <header
        style={{
          height: "55px",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          backgroundColor: tokens.colorBrandBackground,
          padding: "0 1rem",
          flexShrink: 0,
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
          <Logo
            logoUrl={currentTenant?.logo_ath || currentTenant?.logo_path || null}
            alt="Tenant Logo"
            width={24}
            height={24}
            style={{ backgroundColor: "transparent" }}
          />
          <h2 style={{ color: tokens.colorNeutralForegroundOnBrand, margin: 0 }}>{tenantName}</h2>
        </div>
        <AccountMenu onOpenPreferences={() => setIsPreferencesOpen(true)} />
      </header>

      {/* Content area */}
      <div style={{ flex: 1, display: "flex", overflow: "hidden" }}>
        <div style={{ position: "fixed", zIndex: 10000 }}>
          <ErrorDialog />
        </div>

        <NavigationPanel
          isMinimized={isDrawerMinimized}
          setIsMinimized={setDrawerMinimized}
          width={isDrawerMinimized ? 60 : 240}
        />

        <main
          style={{
            flex: 1,
            overflowY: "auto",
            padding: "20px",
            boxSizing: "border-box",
            position: "relative",
          }}
        >
          <Routes>
            <Route path="/" element={<Home />} />
            <Route path="/logout" element={<Logout />} />
            <Route path="/cache" element={<CachePage />} />
            <Route path="/tickets" element={<Tickets />} />
            <Route path="/tickets/new" element={<TicketEditor newTicket={true} />} />
            <Route path="/tickets/:publicId" element={<TicketEditor newTicket={false} />} />
            <Route path="/problems" element={<FeatureNotImplemented FeatureName={t("generic.problems")} />} />
            <Route path="/changes" element={<FeatureNotImplemented FeatureName={t("generic.changes")} />} />
            <Route path="/ci" element={<Ci />} />
            <Route path="/ci/new" element={<CiEditor newCi={true} />} />
            <Route path="/ci/:publicId" element={<CiEditor newCi={false} />} />
            <Route path="/data" element={<Data />} />
            <Route path="/settings" element={<Settings themeName={themeName} setThemeName={setThemeName} />} />
          </Routes>
        </main>
        <PreferencesDrawer
          isOpen={isPreferencesOpen}
          setIsOpen={setIsPreferencesOpen}
          themeName={themeName}
          setThemeName={setThemeName}
        />
      </div>
    </div>
  );
}

function PublicTitleManager({ registerSubdomain }) {
  const { token } = useAuth();
  const hostParts = window.location.hostname.toLowerCase().split(".");
  const hostSubdomain = hostParts.length > 2 ? hostParts[0] : null;
  const resolvedSubdomain = hostSubdomain === registerSubdomain ? null : hostSubdomain;
  const { data: slugTenant } = useTenantBySlugQuery(!token ? resolvedSubdomain : null);

  useEffect(() => {
    if (token) return;
    document.title = getDocumentTitle(slugTenant?.name);
  }, [token, slugTenant?.name]);

  useEffect(() => {
    if (token) return;
    const tenantLogoUrl = resolveTenantLogoUrl(slugTenant?.logo_ath || slugTenant?.logo_path || null);
    setFaviconHref(tenantLogoUrl);
  }, [token, slugTenant?.logo_ath, slugTenant?.logo_path]);

  return null;
}

// Protected route guard
function ProtectedRoute({ themeName, setThemeName }) {
  const { token } = useAuth();
  const location = useLocation();

  if (!token) {
    return <Navigate to="/login" replace state={{ from: location }} />;
  }

  return <AppLayout themeName={themeName} setThemeName={setThemeName} />;
}

function App() {
  const [themeName, setThemeName] = useState(() => {
    return localStorage.getItem("theme") || "light";
  });
  const apiUrl = process.env.REACT_APP_API_URL?.trim();
  const registerSubdomain = (process.env.REACT_APP_REGISTER_SUBDOMAIN || "register").trim().toLowerCase();
  const currentHost = window.location.hostname.toLowerCase();
  const isRegisterHost = currentHost === registerSubdomain || currentHost.startsWith(`${registerSubdomain}.`);

  const theme = themeName === "dark" ? webDarkTheme : webLightTheme;

  return (
    <ErrorDialogProvider>
      <QueryClientProvider client={queryClient}>
        <FluentProvider theme={theme}>
          <AuthProvider>
            {" "}
            {/* wrap all routes, including /auth */}
            <Toaster toasterId={TOASTER_ID} />
            <Router>
              <PublicTitleManager registerSubdomain={registerSubdomain} />
              <Routes>
                {/* Register subdomain root route */}
                {isRegisterHost && <Route path="/" element={<Register />} />}
                {/* Public login route */}
                <Route path="/login" element={<AuthPage apiUrl={apiUrl} />} />
                {/* Public identity password reset route */}
                <Route path="/identity/reset-password" element={<IdentityPasswordReset />} />
                {/* Public identity onboarding route */}
                <Route path="/identity/onboarding" element={<IdentityOnboarding />} />
                {/* Public registration route */}
                <Route path="/register" element={<Register />} />
                {/* Public identity activation route */}
                <Route path="/identity/activate" element={<IdentityActivate />} />
                {/* Public tenant logo redirect route */}
                <Route path="/tenants/:publicTenantId/logo" element={<TenantLogoRedirect />} />
                <Route path="/404" element={<NotFound />} />

                {/* Protected routes - only accessible with token */}
                <Route
                  path="/*"
                  element={
                    isRegisterHost ? (
                      <Navigate to="/" replace />
                    ) : (
                      <ProtectedRoute themeName={themeName} setThemeName={setThemeName} />
                    )
                  }
                />
              </Routes>
            </Router>
          </AuthProvider>
        </FluentProvider>
      </QueryClientProvider>
    </ErrorDialogProvider>
  );
}

export default App;
